import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus,
  Trash2,
  Users,
  Shield,
  Eye,
  Mail,
  User,
  Pencil,
  UserCheck,
} from "lucide-react";

const UsersPage = () => {
  const { authAxios, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "supervisor",
    agent_name: "",
  });

  const [editFormData, setEditFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "supervisor",
    agent_name: "",
  });

  const fetchUsers = async () => {
    try {
      const response = await authAxios.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await authAxios.get("/agents");
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAgents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.password) {
      toast.error("Todos los campos son requeridos");
      return;
    }

    if (formData.role === "agent" && !formData.agent_name) {
      toast.error("Debe seleccionar un agente mapeado para el rol de agente");
      return;
    }

    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        password: formData.password,
        role: formData.role,
        agent_name: formData.role === "agent" ? formData.agent_name : null,
      };

      await authAxios.post("/users", payload);
      toast.success("Usuario creado exitosamente");
      setIsDialogOpen(false);
      setFormData({ email: "", name: "", password: "", role: "supervisor", agent_name: "" });
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al crear usuario";
      toast.error(message);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email,
      name: user.name,
      password: "",
      role: user.role,
      agent_name: user.agent_name || "",
    });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.email || !editFormData.name) {
      toast.error("El nombre y el correo son obligatorios");
      return;
    }

    if (editFormData.role === "agent" && !editFormData.agent_name) {
      toast.error("Debe seleccionar un agente mapeado para el rol de agente");
      return;
    }

    try {
      const payload = {
        email: editFormData.email,
        name: editFormData.name,
        role: editFormData.role,
        agent_name: editFormData.role === "agent" ? editFormData.agent_name : null,
      };

      if (editFormData.password) {
        payload.password = editFormData.password;
      }

      await authAxios.put(`/users/${editingUser.id}`, payload);
      toast.success("Usuario actualizado exitosamente");
      setIsEditOpen(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar usuario";
      toast.error(message);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await authAxios.delete(`/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al eliminar usuario";
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestión de Usuarios</h1>
          <p className="text-slate-500 mt-1">
            Administra los usuarios y sus permisos en la plataforma
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-700" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre Completo</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Juan Pérez"
                    className="pl-10"
                    data-testid="user-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Correo Electrónico</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="juan@empresa.com"
                    className="pl-10"
                    data-testid="user-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="user-password-input"
                />
              </div>

              <div>
                <Label htmlFor="role">Rol</Label>
                <Select
                  value={formData.role}
                  onValueChange={(v) => setFormData({ ...formData, role: v, agent_name: v === "agent" ? formData.agent_name : "" })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supervisor">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Supervisor
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Administrador
                      </div>
                    </SelectItem>
                    <SelectItem value="agent">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        Usuario Regular (Agente)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.role === "admin" 
                    ? "Acceso completo a todas las funciones"
                    : formData.role === "supervisor"
                    ? "Puede ver reportes y crear matrices de evaluación"
                    : "Acceso limitado a su propio dashboard, alertas y llamadas"
                  }
                </p>
              </div>

              {formData.role === "agent" && (
                <div className="space-y-1">
                  <Label htmlFor="agent_name">Agente Mapeado</Label>
                  <Select
                    value={formData.agent_name}
                    onValueChange={(v) => setFormData({ ...formData, agent_name: v })}
                  >
                    <SelectTrigger id="agent_name" data-testid="user-agent-select">
                      <SelectValue placeholder="Seleccionar agente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map((agentName) => (
                        <SelectItem key={agentName} value={agentName}>
                          {agentName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Asocia este usuario con un agente para restringir sus llamadas y dashboard.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="save-user-btn">
                  Crear Usuario
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Nombre Completo</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="Juan Pérez"
                  className="pl-10"
                  data-testid="edit-user-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-email">Correo Electrónico</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  placeholder="juan@empresa.com"
                  className="pl-10"
                  data-testid="edit-user-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-password">Nueva Contraseña (Opcional)</Label>
              <Input
                id="edit-password"
                type="password"
                value={editFormData.password}
                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                placeholder="Dejar en blanco para no cambiar"
                data-testid="edit-user-password-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-role">Rol</Label>
              <Select
                value={editFormData.role}
                onValueChange={(v) => setEditFormData({ ...editFormData, role: v, agent_name: v === "agent" ? editFormData.agent_name : "" })}
              >
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supervisor">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Supervisor
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Administrador
                    </div>
                  </SelectItem>
                  <SelectItem value="agent">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Usuario Regular (Agente)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {editFormData.role === "admin" 
                  ? "Acceso completo a todas las funciones"
                  : editFormData.role === "supervisor"
                  ? "Puede ver reportes y crear matrices de evaluación"
                  : "Acceso limitado a su propio dashboard, alertas y llamadas"
                }
              </p>
            </div>

            {editFormData.role === "agent" && (
              <div className="space-y-1">
                <Label htmlFor="edit-agent_name">Agente Mapeado</Label>
                <Select
                  value={editFormData.agent_name}
                  onValueChange={(v) => setEditFormData({ ...editFormData, agent_name: v })}
                >
                  <SelectTrigger id="edit-agent_name" data-testid="edit-user-agent-select">
                    <SelectValue placeholder="Seleccionar agente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((agentName) => (
                      <SelectItem key={agentName} value={agentName}>
                        {agentName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Asocia este usuario con un agente para restringir sus llamadas y dashboard.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="update-user-btn">
                Guardar Cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-sky-50 border-sky-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-sky-700">{users.length}</p>
              <p className="text-sm text-sky-600">Usuarios Totales</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">
                {users.filter(u => u.role === "admin").length}
              </p>
              <p className="text-sm text-purple-600">Administradores</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">
                {users.filter(u => u.role === "supervisor").length}
              </p>
              <p className="text-sm text-green-600">Supervisores</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">
                {users.filter(u => u.role === "agent").length}
              </p>
              <p className="text-sm text-blue-600">Usuarios Regular (Agente)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card data-testid="users-table-card">
        <CardHeader>
          <CardTitle>Lista de Usuarios</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Agente Mapeado</TableHead>
                <TableHead>Fecha de Creación</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                        <span className="text-sky-600 font-semibold">
                          {user.name?.charAt(0)?.toUpperCase() || "U"}
                        </span>
                      </div>
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{user.email}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        user.role === "admin"
                          ? "bg-purple-100 text-purple-700"
                          : user.role === "supervisor"
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }
                    >
                      {user.role === "admin" ? (
                        <><Shield className="w-3 h-3 mr-1" /> Administrador</>
                      ) : user.role === "supervisor" ? (
                        <><Eye className="w-3 h-3 mr-1" /> Supervisor</>
                      ) : (
                        <><UserCheck className="w-3 h-3 mr-1" /> Agente</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {user.agent_name ? (
                      <span className="font-semibold text-sky-700 bg-sky-50 px-2.5 py-1 rounded-md text-xs">
                        {user.agent_name}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic text-xs">No asignado</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {user.created_at
                      ? format(new Date(user.created_at), "dd MMM yyyy", { locale: es })
                      : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-sky-600 hover:bg-sky-50"
                        onClick={() => handleEditClick(user)}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>

                      {user.id !== currentUser?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. El usuario "{user.name}" será eliminado permanentemente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(user.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No hay usuarios registrados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
